"use client";

import { useApp } from "@/context/AppContext";
import { useMemo, useState, useEffect } from "react";
import { isToday, startOfMonth } from "date-fns";
import { useNavigate } from "react-router-dom";
import CommonHeader from "./common/CommonHeader";
import { MetricsGrid } from "./dashboard/MetricsGrid";
import { RevenueChart } from "./dashboard/RevenueChart";
import { RecentActivities } from "./dashboard/RecentActivities";
import { UpcomingAppointments } from "./dashboard/UpcomingAppointments";
import { TodayBookingsCard } from "./dashboard/TodayBookingsCard";
import { StatusBreakdownCard } from "./dashboard/StatusBreakdownCard";
import { CashByTeamCard } from "./dashboard/CashByTeamCard";
import { MonthlySalesChart } from "./dashboard/MonthlySalesChart";
import { invoiceService, RevenueChartData } from "@/services/invoiceService";
import { appointmentService } from "@/services/appointmentService";
import { dashboardService, TodayStats, CashByTeam, TodayRevenue } from "@/services/dashboardService";
import { Appointment } from "@/types";

export function Dashboard() {
  const navigate = useNavigate();
  const {
    appointments,
    customers,
    subscriptions,
    invoices,
    activities,
    vehicles,
    staff,
  } = useApp();

  // Calculate metrics
  const metrics = useMemo(() => {
    const today = new Date();
    const todayAppointments = appointments.filter((apt) => isToday(apt.date));
    const thisMonthStart = startOfMonth(today);

    // Only count completed appointments for collected revenue
    const todayRevenue = todayAppointments
      .filter((apt) => apt.amount !== null && apt.status === "COMPLETED")
      .reduce((sum, apt) => sum + (apt.amount || 0), 0);

    const todayStats = todayAppointments.reduce(
      (acc, apt) => {
        acc[apt.status] = (acc[apt.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const activeSubscriptions = subscriptions.filter(
      (sub) => sub.status === "active"
    ).length;

    const pendingInvoices = invoices.filter(
      (inv) =>
        inv.paymentStatus === "pending" || inv.paymentStatus === "cancelled"
    );
    const pendingAmount = pendingInvoices.reduce(
      (sum, inv) => sum + inv.total,
      0
    );

    const thisMonthRevenue = invoices
      .filter(
        (inv) => inv.date >= thisMonthStart && inv.paymentStatus === "paid"
      )
      .reduce((sum, inv) => sum + inv.total, 0);

    return {
      todayRevenue,
      todayAppointments: todayAppointments.length,
      todayStats,
      activeSubscriptions,
      pendingPayments: pendingInvoices.length,
      pendingAmount,
      totalCustomers: customers.length,
      thisMonthRevenue,
    };
  }, [appointments, subscriptions, invoices, customers]);

  // Today's stats from API
  const [todayStats, setTodayStats] = useState<TodayStats | null>(null);
  const [todayStatsLoading, setTodayStatsLoading] = useState(true);

  // Today's revenue from API (actual collected vs pending)
  const [todayRevenue, setTodayRevenue] = useState<TodayRevenue | null>(null);
  const [todayRevenueLoading, setTodayRevenueLoading] = useState(true);

  // Cash by team from API
  const [cashByTeam, setCashByTeam] = useState<CashByTeam[]>([]);
  const [cashByTeamLoading, setCashByTeamLoading] = useState(true);

  // Upcoming appointments from API
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [upcomingAppointmentsLoading, setUpcomingAppointmentsLoading] = useState(true);

  useEffect(() => {
    const fetchTodayStats = async () => {
      try {
        setTodayStatsLoading(true);
        const data = await dashboardService.getTodayStats();
        setTodayStats(data);
      } catch (error) {
        console.error("Failed to fetch today's stats:", error);
        setTodayStats(null);
      } finally {
        setTodayStatsLoading(false);
      }
    };

    const fetchTodayRevenue = async () => {
      try {
        setTodayRevenueLoading(true);
        const data = await dashboardService.getTodayRevenue();
        setTodayRevenue(data);
      } catch (error) {
        console.error("Failed to fetch today's revenue:", error);
        setTodayRevenue(null);
      } finally {
        setTodayRevenueLoading(false);
      }
    };

    const fetchCashByTeam = async () => {
      try {
        setCashByTeamLoading(true);
        const data = await dashboardService.getCashByTeam();
        setCashByTeam(data);
      } catch (error) {
        console.error("Failed to fetch cash by team:", error);
        setCashByTeam([]);
      } finally {
        setCashByTeamLoading(false);
      }
    };

    const fetchUpcomingAppointments = async () => {
      try {
        setUpcomingAppointmentsLoading(true);
        // Get PENDING appointments (default) or PENDING,CONFIRMED if needed
        const data = await appointmentService.getUpcomingAppointments(10);
        setUpcomingAppointments(data);
      } catch (error) {
        console.error("Failed to fetch upcoming appointments:", error);
        setUpcomingAppointments([]);
      } finally {
        setUpcomingAppointmentsLoading(false);
      }
    };

    fetchTodayStats();
    fetchTodayRevenue();
    fetchCashByTeam();
    fetchUpcomingAppointments();
  }, []); // Empty dependency array - fetch once on mount

  // Revenue chart data from API
  const [revenueChartData, setRevenueChartData] = useState<RevenueChartData[]>([]);
  const [revenueChartLoading, setRevenueChartLoading] = useState(true);

  useEffect(() => {
    const fetchRevenueChart = async () => {
      try {
        setRevenueChartLoading(true);
        const data = await invoiceService.getRevenueChartData(7);
        setRevenueChartData(data);
      } catch (error) {
        console.error("Failed to fetch revenue chart data:", error);
        // Set empty array on error to prevent chart crash
        setRevenueChartData([]);
      } finally {
        setRevenueChartLoading(false);
      }
    };

    fetchRevenueChart();
  }, []); // Empty dependency array - fetch once on mount

  return (
    <div className="space-y-8 max-w-[1600px]">
      {/* Header */}
      <CommonHeader
        title="Dashboard"
        description="Welcome to WeClean Car Wash Management"
        onClick={() => navigate("/appointments")}
        buttonText="New Appointment"
      />

      {/* Metrics Grid */}
      <MetricsGrid
        todayRevenue={todayRevenue}
        todayRevenueLoading={todayRevenueLoading}
        todayAppointments={metrics.todayAppointments}
        todayStats={metrics.todayStats}
        totalCustomers={metrics.totalCustomers}
        vehiclesCount={vehicles.length}
        activeSubscriptions={metrics.activeSubscriptions}
      />

      {/* Today's Bookings and Status Breakdown */}
      <div className="grid gap-6 lg:grid-cols-2">
        <TodayBookingsCard stats={todayStats} loading={todayStatsLoading} />
        <StatusBreakdownCard stats={todayStats} loading={todayStatsLoading} />
      </div>

      {/* Monthly Sales Chart and Cash by Team */}
      <div className="grid gap-6 lg:grid-cols-7">
        <MonthlySalesChart />
        <div className="lg:col-span-3">
          <CashByTeamCard data={cashByTeam} loading={cashByTeamLoading} />
        </div>
      </div>

      {/* Revenue Chart and Recent Activities */}
      <div className="grid gap-6 lg:grid-cols-7">
        <RevenueChart data={revenueChartData} />
        <RecentActivities activities={activities} />
      </div>

      {/* Upcoming Appointments */}
      <UpcomingAppointments
        appointments={upcomingAppointments}
        customers={customers}
        vehicles={vehicles}
        staff={staff}
      />
    </div>
  );
}
